import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, Observable, combineLatest, map, startWith, switchMap } from 'rxjs';

import { RegionsService, RegionsListResponse } from '../../api/regions.service';
import { PointsOfInterestService, PoisListResponse } from '../../api/points-of-interest.service';
import { QuestsService, QuestsListResponse } from '../../api/quests.service';
import { TomesService, TomesListResponse } from '../../api/tomes.service';
import { TomePagesService, TomePagesListResponse } from '../../api/tome-pages.service';
import { QuestRequirementsService, QuestRequirementsListResponse } from '../../api/quest-requirements.service';
import {
	NonPlayerCharactersService,
	NonPlayerCharactersListResponse,
} from '../../api/non-player-characters.service';
import { PlayerCharactersService, PlayerCharactersListResponse } from '../../api/player-characters.service';
import { FactionsService, FactionsListResponse } from '../../api/factions.service';
import { GodsService, GodsListResponse } from '../../api/gods.service';
import { PantheonsService, PantheonsListResponse } from '../../api/pantheons.service';
import { DomainsService, DomainsListResponse } from '../../api/domains.service';
import { EncountersService, EncountersListResponse } from '../../api/encounters.service';
import { ItemsService, ItemsListResponse } from '../../api/items.service';
import { GoodsServicesService, GoodsServicesListResponse } from '../../api/goods-services.service';
import { MerchantsService, MerchantsListResponse } from '../../api/merchants.service';

type ListItem = {
	id: string;
	name: string;
	subtitle?: string;
	meta?: string;
};

type Vm = {
	title: string;
	kind: string;
	canCreate: boolean;
	items: ListItem[];
};

@Component({
	selector: 'of-entity-list-page',
	standalone: true,
	imports: [CommonModule, RouterModule],
	styleUrls: ['./entity-list.page.scss'],
	template: `
		<section class="entity-list-shell" *ngIf="vm$ | async as vm; else loading">
			<header class="entity-list-header">
				<div>
					<p class="kicker">Workspace</p>
					<h2>{{ vm.title }}</h2>
				</div>
				<div class="header-actions">
					<button type="button" class="btn-outline" (click)="back()">Back</button>
					<button type="button" class="btn-primary-gradient" *ngIf="vm.canCreate" (click)="createNew()">New</button>
				</div>
			</header>

			<div class="list-card">
				<div class="list-meta">
					<div class="support-text">Total: {{ vm.items.length }}</div>
				</div>

				<div *ngIf="vm.items.length === 0" class="empty-state">
					<p class="support-text">No records yet.</p>
					<button *ngIf="vm.canCreate" type="button" class="btn-outline" (click)="createNew()">Create the first</button>
				</div>

				<div class="list-grid" *ngIf="vm.items.length > 0">
					<div class="list-row" *ngFor="let item of vm.items">
						<div class="list-row__main">
							<div class="list-row__name">{{ item.name }}</div>
							<div class="list-row__meta">
								<span *ngIf="item.subtitle">{{ item.subtitle }}</span>
								<span *ngIf="item.subtitle && item.meta"> · </span>
								<span *ngIf="item.meta">{{ item.meta }}</span>
							</div>
						</div>
						<div class="list-row__actions">
							<button type="button" class="btn-outline slim" (click)="open(item.id)">Open</button>
							<button type="button" class="btn-outline slim" (click)="delete(item.id)">Delete</button>
						</div>
					</div>
				</div>
			</div>
		</section>

		<ng-template #loading>
			<section class="entity-list-shell">
				<p class="support-text">Loading...</p>
			</section>
		</ng-template>
	`,
})
export class EntityListPage {
	entity: string;
	private readonly refresh$ = new BehaviorSubject<void>(undefined);
	vm$: Observable<Vm>;
	private worldId = '';
	private tomeIdFilter: string | null = null;
	private questIdFilter: string | null = null;

	constructor(
		route: ActivatedRoute,
		private readonly router: Router,
		private readonly regions: RegionsService,
		private readonly pois: PointsOfInterestService,
		private readonly quests: QuestsService,
		private readonly tomes: TomesService,
		private readonly tomePages: TomePagesService,
		private readonly questRequirements: QuestRequirementsService,
		private readonly npcs: NonPlayerCharactersService,
		private readonly playerCharacters: PlayerCharactersService,
		private readonly factions: FactionsService,
		private readonly gods: GodsService,
		private readonly pantheons: PantheonsService,
		private readonly domains: DomainsService,
		private readonly encounters: EncountersService,
		private readonly items: ItemsService,
		private readonly goodsServices: GoodsServicesService,
		private readonly merchants: MerchantsService
	) {
		this.entity = route.snapshot.paramMap.get('entity') ?? 'entities';

		const worldId$ = route.parent!.paramMap.pipe(map((p) => p.get('worldId') ?? ''));
		const entity$ = route.paramMap.pipe(map((p) => p.get('entity') ?? 'entities'));
		const tomeId$ = route.queryParamMap.pipe(map((p) => p.get('tomeId')));
		const questId$ = route.queryParamMap.pipe(map((p) => p.get('questId')));
		const refresh$ = this.refresh$.pipe(startWith(undefined));

		this.vm$ = combineLatest([worldId$, entity$, refresh$, tomeId$, questId$]).pipe(
			switchMap(([worldId, entity, _refresh, tomeId, questId]) => {
				this.worldId = worldId;
				this.entity = entity;
				this.tomeIdFilter = tomeId;
				this.questIdFilter = questId;

				const unknown: Vm = {
					title: entity.replace(/-/g, ' '),
					kind: 'unknown',
					canCreate: false,
					items: [],
				};

				if (entity === 'regions') {
					return this.regions.list(worldId).pipe(
						map((data) => ({
							title: 'Regions',
							kind: 'regions',
							canCreate: true,
							items: this.mapItems('regions', data),
						}))
					);
				}

				if (entity === 'points-of-interest') {
					return this.pois.list(worldId).pipe(
						map((data) => ({
							title: 'Points of Interest',
							kind: 'points-of-interest',
							canCreate: true,
							items: this.mapItems('points-of-interest', data),
						}))
					);
				}

				if (entity === 'quests') {
					return this.quests.list(worldId).pipe(
						map((data) => ({
							title: 'Quests',
							kind: 'quests',
							canCreate: true,
							items: this.mapItems('quests', data),
						}))
					);
				}

				if (entity === 'tomes') {
					return this.tomes.list(worldId).pipe(
						map((data) => ({
							title: 'Tomes',
							kind: 'tomes',
							canCreate: true,
							items: this.mapItems('tomes', data),
						}))
					);
				}

				if (entity === 'tome-pages') {
					return this.tomePages.list(worldId, { tomeId: tomeId ?? undefined }).pipe(
						map((data) => ({
							title: 'Tome Pages',
							kind: 'tome-pages',
							canCreate: true,
							items: this.mapItems('tome-pages', data),
						}))
					);
				}

				if (entity === 'quest-requirements') {
					return this.questRequirements.list(worldId, { questId: questId ?? undefined }).pipe(
						map((data) => ({
							title: 'Quest Requirements',
							kind: 'quest-requirements',
							canCreate: true,
							items: this.mapItems('quest-requirements', data),
						}))
					);
				}

				if (entity === 'non-player-characters') {
					return this.npcs.list(worldId).pipe(
						map((data) => ({
							title: 'Non-Player Characters',
							kind: 'non-player-characters',
							canCreate: true,
							items: this.mapItems('non-player-characters', data),
						}))
					);
				}

				if (entity === 'player-characters') {
					return this.playerCharacters.list(worldId).pipe(
						map((data) => ({
							title: 'Player Characters',
							kind: 'player-characters',
							canCreate: true,
							items: this.mapItems('player-characters', data),
						}))
					);
				}

				if (entity === 'factions') {
					return this.factions.list(worldId).pipe(
						map((data) => ({
							title: 'Factions',
							kind: 'factions',
							canCreate: true,
							items: this.mapItems('factions', data),
						}))
					);
				}

				if (entity === 'gods') {
					return this.gods.list(worldId).pipe(
						map((data) => ({
							title: 'Gods',
							kind: 'gods',
							canCreate: true,
							items: this.mapItems('gods', data),
						}))
					);
				}

				if (entity === 'pantheons') {
					return this.pantheons.list(worldId).pipe(
						map((data) => ({
							title: 'Pantheons',
							kind: 'pantheons',
							canCreate: true,
							items: this.mapItems('pantheons', data),
						}))
					);
				}

				if (entity === 'domains') {
					return this.domains.list(worldId).pipe(
						map((data) => ({
							title: 'Domains',
							kind: 'domains',
							canCreate: true,
							items: this.mapItems('domains', data),
						}))
					);
				}

				if (entity === 'encounters') {
					return this.encounters.list(worldId).pipe(
						map((data) => ({
							title: 'Encounters',
							kind: 'encounters',
							canCreate: true,
							items: this.mapItems('encounters', data),
						}))
					);
				}

				if (entity === 'items') {
					return this.items.list(worldId).pipe(
						map((data) => ({
							title: 'Items',
							kind: 'items',
							canCreate: true,
							items: this.mapItems('items', data),
						}))
					);
				}

				if (entity === 'goods-services') {
					return this.goodsServices.list(worldId).pipe(
						map((data) => ({
							title: 'Goods & Services',
							kind: 'goods-services',
							canCreate: true,
							items: this.mapItems('goods-services', data),
						}))
					);
				}

				if (entity === 'merchants') {
					return this.merchants.list(worldId).pipe(
						map((data) => ({
							title: 'Merchants',
							kind: 'merchants',
							canCreate: true,
							items: this.mapItems('merchants', data),
						}))
					);
				}

				return new Observable<Vm>((sub) => {
					sub.next(unknown);
					sub.complete();
				});
			})
		);
	}

	refresh() {
		this.refresh$.next();
	}

	back() {
		void this.router.navigate(['/workspace/worlds', this.worldId]);
	}

	createNew() {
		if (this.entity === 'tome-pages' && this.tomeIdFilter) {
			void this.router.navigate(['/workspace/worlds', this.worldId, this.entity, 'new'], { queryParams: { tomeId: this.tomeIdFilter } });
			return;
		}
		if (this.entity === 'quest-requirements' && this.questIdFilter) {
			void this.router.navigate(['/workspace/worlds', this.worldId, this.entity, 'new'], { queryParams: { questId: this.questIdFilter } });
			return;
		}
		void this.router.navigate(['/workspace/worlds', this.worldId, this.entity, 'new']);
	}

	open(id: string) {
		void this.router.navigate(['/workspace/worlds', this.worldId, this.entity, id]);
	}

	delete(id: string) {
		const worldId = this.worldId;
		if (this.entity === 'regions') {
			this.regions.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'points-of-interest') {
			this.pois.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'quests') {
			this.quests.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'tomes') {
			this.tomes.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'tome-pages') {
			this.tomePages.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'quest-requirements') {
			this.questRequirements.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'non-player-characters') {
			this.npcs.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'player-characters') {
			this.playerCharacters.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'factions') {
			this.factions.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'gods') {
			this.gods.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'pantheons') {
			this.pantheons.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'domains') {
			this.domains.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'encounters') {
			this.encounters.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'items') {
			this.items.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'goods-services') {
			this.goodsServices.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
		if (this.entity === 'merchants') {
			this.merchants.delete(worldId, id).subscribe({ next: () => this.refresh(), error: () => this.refresh() });
			return;
		}
	}

	private mapItems(kind: string, data: any): ListItem[] {
		switch (kind) {
			case 'regions':
				return (data as RegionsListResponse).regions?.map((r) => ({
					id: r.id,
					name: r.name,
					subtitle: '',
					meta: r.updatedAt ? `Updated ${new Date(r.updatedAt).toLocaleDateString()}` : '',
				})) ?? [];
			case 'points-of-interest':
				return (data as PoisListResponse).pois?.map((p) => ({
					id: p.id,
					name: p.name,
					subtitle: p.typeName ?? '—',
					meta: p.regionName ?? '—',
				})) ?? [];
			case 'goods-services':
				return (data as GoodsServicesListResponse).goods?.map((g) => ({
					id: g.id,
					name: g.name,
					subtitle: g.category ?? '—',
					meta: g.sourcePoiName ?? g.sourceRegionName ?? '—',
				})) ?? [];
			case 'merchants':
				return (data as MerchantsListResponse).merchants?.map((m) => ({
					id: m.id,
					name: m.name,
					subtitle: m.npcName ?? m.poiName ?? m.factionName ?? '—',
					meta: `Inventory ${m.inventoryCount ?? 0}`,
				})) ?? [];
			case 'quests':
				return (data as QuestsListResponse).quests?.map((q) => ({
					id: q.id,
					name: q.name,
					subtitle: q.status ?? 'planned',
					meta: q.factionName ?? q.npcName ?? q.godName ?? '—',
				})) ?? [];
			case 'tomes':
				return (data as TomesListResponse).tomes?.map((t) => ({
					id: t.id,
					name: t.name,
					subtitle: t.rarity ?? '—',
					meta: `${t.poiName ?? '—'} · ${t.regionName ?? '—'}`,
				})) ?? [];
			case 'tome-pages':
				return (data as TomePagesListResponse).tomePages?.map((t) => ({
					id: t.id,
					name: t.name,
					subtitle: t.tomeName ?? '—',
					meta: t.updatedAt ? `Updated ${new Date(t.updatedAt).toLocaleDateString()}` : '',
				})) ?? [];
			case 'quest-requirements':
				return (data as QuestRequirementsListResponse).questRequirements?.map((qr) => ({
					id: qr.id,
					name: qr.name,
					subtitle: qr.status ?? 'Not Known',
					meta: qr.questName ?? '—',
				})) ?? [];
			case 'non-player-characters':
				return (data as NonPlayerCharactersListResponse).npcs?.map((npc) => ({
					id: npc.id,
					name: npc.name,
					subtitle: npc.homePoiName ?? npc.factionName ?? '—',
					meta: npc.updatedAt ? `Updated ${new Date(npc.updatedAt).toLocaleDateString()}` : '',
				})) ?? [];
			case 'player-characters':
				return (data as PlayerCharactersListResponse).playerCharacters?.map((pc) => ({
					id: pc.id,
					name: pc.name,
					subtitle: pc.status ?? 'active',
					meta: pc.userName ?? '—',
				})) ?? [];
			case 'factions':
				return (data as FactionsListResponse).factions?.map((f) => ({
					id: f.id,
					name: f.name,
					subtitle: f.regionNames ?? f.poiNames ?? f.npcNames ?? '—',
				})) ?? [];
			case 'gods':
				return (data as GodsListResponse).gods?.map((g) => ({
					id: g.id,
					name: g.name,
					subtitle: g.pantheonName ?? '—',
					meta: (g.domains?.map((d) => d.name).filter(Boolean).join(', ')) || '—',
				})) ?? [];
			case 'pantheons':
				return (data as PantheonsListResponse).pantheons?.map((p) => ({
					id: p.id,
					name: p.name,
					subtitle: p.currentState ?? '—',
					meta: p.updatedAt ? `Updated ${new Date(p.updatedAt).toLocaleDateString()}` : '',
				})) ?? [];
			case 'domains':
				return (data as DomainsListResponse).domains?.map((d) => ({
					id: d.id,
					name: d.name,
					subtitle: d.pantheonName ?? '—',
					meta: d.godCount ? `${d.godCount} gods` : '',
				})) ?? [];
			case 'encounters':
				return (data as EncountersListResponse).encounters?.map((e) => ({
					id: e.id,
					name: e.name,
					subtitle: e.environment ?? '—',
					meta: e.updatedAt ? `Updated ${new Date(e.updatedAt).toLocaleDateString()}` : '',
				})) ?? [];
			case 'items':
				return (data as ItemsListResponse).items?.map((i) => ({
					id: i.id,
					name: i.name,
					subtitle: i.itemTypeName ?? i.category ?? '—',
					meta: `${i.rarity ?? '—'}${i.sourcePoiName ? ' · ' + i.sourcePoiName : ''}`,
				})) ?? [];
			default:
				return [];
		}
	}
}
